// Re-export shared types and helpers
export type { WordTiming, KenBurnsConfig, Scene, LogoSceneConfig } from '../shared/types';
export { msToFrames, getCurrentScene, getVisibleWords, KEN_BURNS_PRESETS } from '../shared/types';
import type { WordTiming, Scene } from '../shared/types';
import { KEN_BURNS_PRESETS } from '../shared/types';

// Color palettes — Second-Month-Overwhelm theme (warning red → green growth)
export const SCENE_COLORS = {
  scene1: { primaryColor: '#FCD34D', accentColor: '#ffffff' }, // gold (incoming text)
  scene2: { primaryColor: '#22C55E', accentColor: '#FCD34D' }, // green (growth, month 2)
  scene3: { primaryColor: '#EF4444', accentColor: '#ffffff' }, // red (BEGGING / pause - dramatic)
  scene4: { primaryColor: '#F59E0B', accentColor: '#ffffff' }, // amber (overwhelm)
  scene5: { primaryColor: '#3B82F6', accentColor: '#ffffff' }, // blue (last agency / contrast)
  scene6: { primaryColor: '#FCD34D', accentColor: '#ffffff' }, // gold (CTA)
  logo: { primaryColor: '#ffffff', accentColor: '#FCD34D' },
};

// Scene background images — reused from existing PVM compositions for paver-locked visuals
export const SCENE_IMAGES = {
  scene1: 'images/pvm-quiet-phone/scene2-ringing-phone.png',
  scene2: 'images/pvm-3800-to-12000/scene4-growth.png',
  scene3: 'images/pvm-stop-feeding-shared-leads/scene2-shared.png',
  scene4: 'images/pvm-quiet-phone/scene4-paving-job.png',
  scene5: 'images/pvm-quiet-phone/scene1-quiet-phone.png',
  scene6: 'images/pvm-3800-to-12000/scene6-report.png',
};

export const REPORT_SLIDES = [
  'images/pvm-quiet-phone/report-slide-01.png',
  'images/pvm-quiet-phone/report-slide-05.png',
  'images/pvm-quiet-phone/report-slide-07.png',
  'images/pvm-quiet-phone/report-slide-12.png',
];

// Words that get dramatic animation (larger + glow)
export const DRAMATIC_WORDS = ['begging', 'pause', 'buried.', 'opposite', 'problem.', 'Free', 'Report.'];

// Word timings — sourced from scripts/pvm-second-month-pause-word-timings.json
export const WORD_TIMINGS: WordTiming[] = [
  // Scene 1: A paving client just texted us. (0–1580ms)
  { text: 'A', startMs: 0, endMs: 80 },
  { text: 'paving', startMs: 80, endMs: 380 },
  { text: 'client', startMs: 380, endMs: 640 },
  { text: 'just', startMs: 640, endMs: 880 },
  { text: 'texted', startMs: 880, endMs: 1260 },
  { text: 'us.', startMs: 1260, endMs: 1580 },

  // Scene 2: Her second month with us. (1580–3060ms)
  { text: 'Her', startMs: 1580, endMs: 2000 },
  { text: 'second', startMs: 2000, endMs: 2260 },
  { text: 'month', startMs: 2260, endMs: 2520 },
  { text: 'with', startMs: 2520, endMs: 2700 },
  { text: 'us.', startMs: 2700, endMs: 3060 },

  // Scene 3: She's begging us to pause her ads. (3060–4980ms)
  { text: "She's", startMs: 3060, endMs: 3560 },
  { text: 'begging', startMs: 3560, endMs: 3880 },
  { text: 'us', startMs: 3880, endMs: 4120 },
  { text: 'to', startMs: 4120, endMs: 4260 },
  { text: 'pause', startMs: 4260, endMs: 4500 },
  { text: 'her', startMs: 4500, endMs: 4640 },
  { text: 'ads.', startMs: 4640, endMs: 4980 },

  // Scene 4: She can't keep up with the asphalt estimates. Her brother is buried. (4980–8960ms)
  { text: 'She', startMs: 4980, endMs: 5100 },
  { text: "can't", startMs: 5100, endMs: 5340 },
  { text: 'keep', startMs: 5340, endMs: 5500 },
  { text: 'up', startMs: 5500, endMs: 5700 },
  { text: 'with', startMs: 5700, endMs: 5820 },
  { text: 'the', startMs: 5820, endMs: 5920 },
  { text: 'asphalt', startMs: 5920, endMs: 6200 },
  { text: 'estimates.', startMs: 6200, endMs: 7080 },
  { text: 'Her', startMs: 7080, endMs: 7920 },
  { text: 'brother', startMs: 7920, endMs: 8220 },
  { text: 'is', startMs: 8220, endMs: 8360 },
  { text: 'buried.', startMs: 8360, endMs: 8960 },

  // Scene 5: Two months in, and if your last agency couldn't even get the phone to ring, this is the opposite problem. (8960–14300ms)
  { text: 'Two', startMs: 8960, endMs: 9520 },
  { text: 'months', startMs: 9520, endMs: 9700 },
  { text: 'in,', startMs: 9700, endMs: 10000 },
  { text: 'and', startMs: 10000, endMs: 10100 },
  { text: 'if', startMs: 10100, endMs: 10180 },
  { text: 'your', startMs: 10180, endMs: 10320 },
  { text: 'last', startMs: 10320, endMs: 10500 },
  { text: 'agency', startMs: 10500, endMs: 10900 },
  { text: "couldn't", startMs: 10900, endMs: 11240 },
  { text: 'even', startMs: 11240, endMs: 11360 },
  { text: 'get', startMs: 11360, endMs: 11540 },
  { text: 'the', startMs: 11540, endMs: 11680 },
  { text: 'phone', startMs: 11680, endMs: 11900 },
  { text: 'to', startMs: 11900, endMs: 12060 },
  { text: 'ring,', startMs: 12060, endMs: 12380 },
  { text: 'this', startMs: 12380, endMs: 12920 },
  { text: 'is', startMs: 12920, endMs: 13060 },
  { text: 'the', startMs: 13060, endMs: 13160 },
  { text: 'opposite', startMs: 13160, endMs: 13440 },
  { text: 'problem.', startMs: 13440, endMs: 14300 },

  // Scene 6: Only book a call if you can staff the work. Free market report. 10 minutes. Link below. (14300–20460ms)
  { text: 'Only', startMs: 14300, endMs: 14960 },
  { text: 'book', startMs: 14960, endMs: 15140 },
  { text: 'a', startMs: 15140, endMs: 15300 },
  { text: 'call', startMs: 15300, endMs: 15480 },
  { text: 'if', startMs: 15480, endMs: 15620 },
  { text: 'you', startMs: 15620, endMs: 15720 },
  { text: 'can', startMs: 15720, endMs: 15840 },
  { text: 'staff', startMs: 15840, endMs: 16140 },
  { text: 'the', startMs: 16140, endMs: 16300 },
  { text: 'work.', startMs: 16300, endMs: 17100 },
  { text: 'Free', startMs: 17100, endMs: 18000 },
  { text: 'Market', startMs: 18000, endMs: 18260 },
  { text: 'Report.', startMs: 18260, endMs: 18820 },
  { text: 'Ten', startMs: 18820, endMs: 19120 },
  { text: 'minutes.', startMs: 19120, endMs: 19640 },
  { text: 'Link', startMs: 19640, endMs: 19920 },
  { text: 'below.', startMs: 19920, endMs: 20460 },
];

export const SCENES: Scene[] = [
  {
    id: 1,
    name: 'Texted Us',
    startMs: 0,
    endMs: 1580,
    words: WORD_TIMINGS.slice(0, 6),
    ...SCENE_COLORS.scene1,
    backgroundImage: SCENE_IMAGES.scene1,
    kenBurns: KEN_BURNS_PRESETS.zoomIn,
  },
  {
    id: 2,
    name: 'Second Month',
    startMs: 1580,
    endMs: 3060,
    words: WORD_TIMINGS.slice(6, 11),
    ...SCENE_COLORS.scene2,
    backgroundImage: SCENE_IMAGES.scene2,
    kenBurns: KEN_BURNS_PRESETS.dramaticZoom,
  },
  {
    id: 3,
    name: 'Begging Pause',
    startMs: 3060,
    endMs: 4980,
    words: WORD_TIMINGS.slice(11, 18),
    ...SCENE_COLORS.scene3,
    backgroundImage: SCENE_IMAGES.scene3,
    kenBurns: KEN_BURNS_PRESETS.panRight,
  },
  {
    id: 4,
    name: 'Brother Buried',
    startMs: 4980,
    endMs: 8960,
    words: WORD_TIMINGS.slice(18, 30),
    ...SCENE_COLORS.scene4,
    backgroundImage: SCENE_IMAGES.scene4,
    kenBurns: KEN_BURNS_PRESETS.zoomOut,
  },
  {
    id: 5,
    name: 'Last Agency / Opposite Problem',
    startMs: 8960,
    endMs: 14300,
    words: WORD_TIMINGS.slice(30, 50),
    ...SCENE_COLORS.scene5,
    backgroundImage: SCENE_IMAGES.scene5,
    kenBurns: KEN_BURNS_PRESETS.panLeft,
  },
  {
    id: 6,
    name: 'CTA',
    startMs: 14300,
    endMs: 20460,
    words: WORD_TIMINGS.slice(50),
    ...SCENE_COLORS.scene6,
    backgroundImage: SCENE_IMAGES.scene6,
    kenBurns: KEN_BURNS_PRESETS.zoomWithTilt,
  },
];

export const LOGO_SCENE = {
  startMs: 20460,
  endMs: 25560, // +5.1s logo animation
  ...SCENE_COLORS.logo,
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
